/**
 * Employment wrapper card (design §8 / fullscreen-spec §13). STATUS-AWARE
 * branches ported from v1 (data-map §11, user override of parity §14 gap 1),
 * shared by BOTH screening kinds — step data is screening-agnostic:
 * - currently-employed: Current card (now incl. Source Of Income =
 *   currentSourceOfIncome, v1 d815ac5a:5417) + Previous card ONLY when any
 *   previous* field is non-empty (v1:6446-6450) + "Additional Employment N"
 *   cards for entries with ≥1 non-empty value (EmploymentInfoSection:36-41,
 *   217-237).
 * - not-employed: single Previous card (v1:5421-5439). Government
 *   Assistance is NOT on this card — it is a student-only Additional
 *   Question, catalog-gated (`government_assistance`).
 * - no-employment-history: one card with Source Of Income / Annual
 *   Income (noHistoryAnnualIncome). Government Assistance is student-only
 *   Additional Questions, same catalog gate.
 * - other/missing status: legacy ungated two-card render (branch table).
 * Maternity/Paternity is a CROSS-BRANCH row (data-map §11: render whenever
 * present, any status — v1 EmploymentInfoSection:176-190 mounts it before
 * every branch card): the employed/legacy Current card keeps it always-on
 * when the household listed children (the form only asks then —
 * EmploymentStep maternityLeaveRequired); with no children the field is
 * hidden. The not-employed and no-history cards stay presence-gated.
 * Status pill beside the wrapper label uses v1's label strings (display.ts
 * employmentStatusLabel); green/amber/neutral tones are a recorded design
 * extrapolation — the export only shows the employed variant. v1 hides
 * empty fields; v2 house style renders '-' INSIDE whichever cards a branch
 * shows (recorded deviation).
 */
import { BriefcaseBusiness } from 'lucide-react'
import type { ReactNode } from 'react'
import { hasAnyChildren } from '#app/components/screening/has-any-children'
import { deriveCustomQuestionRows } from '#app/utils/application-detail-v2/custom-question-display'
import {
	employmentStatusLabel,
	formatMoneyValue,
	hasAnyNonEmptyValue,
	optionLabel,
	questionLabel,
	tenureLabel,
	workAuthStatusHasExpiration,
	yesNoLabel,
} from '#app/utils/application-detail-v2/display'
import { deriveStudentFinancialRows } from '#app/utils/application-detail-v2/student-housing-display'
import type { ParsedStepData } from '#app/utils/application-detail-v2/types'
import { useFormatDate } from '#app/utils/application-detail-v2/vendor/use-format-date'
import type { ResolvedWorkAuthorization } from '#app/utils/application-detail-v2/work-authorization'
import type { FormConfigV1 } from '#app/utils/application-form/config'
import {
	isEmployerContactFieldOn,
	isPrebuiltQuestionOn,
} from '#app/utils/application-form/form-gates'
import { cn } from '#app/utils/misc'
import { formatPhoneNumberWithFallback } from '#app/utils/phone-formatting'
import {
	DETAIL_CARD,
	FieldBox,
	IconSquare,
	PastelChip,
	type PastelTone,
	SectionCard,
} from '../primitives'
import { CustomQuestionAnswerList } from './CustomQuestionAnswerList'

/** One employment record's display values — current, previous, or an
 * additional entry (v1 EmploymentEntry, d815ac5a:5371-5385). Type alias on
 * purpose: it must stay assignable to hasAnyNonEmptyValue's record param. */
type EmploymentEntry = {
	companyName?: string
	occupation?: string
	annualIncome?: string
	companyPhone?: string
	workAddress?: string
	yearsOfEmployment?: string
	employmentType?: string
	sourceOfIncome?: string
}

/** Shared FieldBox set of every employment card (design §8 order; Source Of
 * Income slots before the full-width address so the Maternity + Government
 * Assistance design pair stays last). */
function EmploymentFields({
	entry,
	companyLabel = 'Company',
	withSourceOfIncome = false,
	phoneFieldOn = true,
	workAddressFieldOn = true,
	children,
}: {
	entry: EmploymentEntry
	companyLabel?: string
	withSourceOfIncome?: boolean
	/** Employer-contact template gates (TT-65): a box renders when the frozen
	 * config enables the field OR the entry carries a value (collected answers
	 * are never hidden). Legacy configs default both on → today's layout. */
	phoneFieldOn?: boolean
	workAddressFieldOn?: boolean
	children?: ReactNode
}) {
	const showPhone = Boolean(entry.companyPhone?.trim()) || phoneFieldOn
	const showWorkAddress =
		Boolean(entry.workAddress?.trim()) || workAddressFieldOn
	return (
		<>
			<FieldBox label={companyLabel} value={entry.companyName} />
			<FieldBox label="Role" value={entry.occupation} />
			<FieldBox
				label="Annual Income"
				value={formatMoneyValue(entry.annualIncome)}
			/>
			<FieldBox label="Tenure" value={tenureLabel(entry.yearsOfEmployment)} />
			<FieldBox
				label="Employment Type"
				value={optionLabel(entry.employmentType)}
			/>
			{showPhone ? (
				<FieldBox
					label="Company Phone"
					value={formatPhoneNumberWithFallback(entry.companyPhone)}
				/>
			) : null}
			{showWorkAddress ? (
				<FieldBox label="Work Address" value={entry.workAddress} fullWidth />
			) : null}
			{children}
		</>
	)
}

export function Employment({
	employment,
	propertyStep,
	formConfig,
	workAuth,
	showStudentExtras,
}: {
	employment: ParsedStepData['employment']
	/** Household step — maternity is asked only when a child is listed. */
	propertyStep?: ParsedStepData['property']
	/** The application's frozen Application Form config (TT-65) — gates the
	 * employer-contact fields, student prebuilt questions, and custom Q&A. */
	formConfig: FormConfigV1
	/** Resolved Work Authorization (QA 2026-09: displayed here, not under
	 * Rental Status) — employment-step answer first, legacy rental-step
	 * answers as fallback (resolveWorkAuthorization). */
	workAuth?: ResolvedWorkAuthorization
	verifyIncomeStep?: ParsedStepData['verify-income']
	// When true: render student-housing only extras (student/parental
	// assistance). Bank/verify-income is not shown here.
	showStudentExtras?: boolean
}) {
	const { formatDate, formatShortDate } = useFormatDate()
	// Passthrough student keys live outside EmploymentEntry — read them through
	// one null-safe record so a MISSING employment step can't crash the page
	// (the bare `employment as Record` cast threw for incomplete applications).
	const employmentExtras = (employment ?? {}) as Record<string, unknown>
	const employerPhoneOn = isEmployerContactFieldOn(formConfig, 'phone')
	const employerWorkAddressOn = isEmployerContactFieldOn(
		formConfig,
		'work_address',
	)
	const status = employment?.employmentStatus?.trim() ?? ''
	const statusLabel = employmentStatusLabel(status)
	// 'employed' is the LEGACY spelling of 'currently-employed' (31 staging
	// applications carry it). Without the alias none of the status branches
	// match, `hasCards` is false, and the whole card grid silently disappears —
	// the applicant's employer, income and tenure never render.
	const isEmployed = status === 'currently-employed' || status === 'employed'
	const isNotEmployed = status === 'not-employed'
	const isNoHistory = status === 'no-employment-history'
	const pillTone: PastelTone = isEmployed
		? 'green'
		: isNotEmployed
			? 'amber'
			: 'neutral'

	const maternityDate =
		employment?.maternityLeaveFromDate || employment?.maternityLeaveFrom
	const maternityValue =
		employment?.maternityLeave === 'yes'
			? maternityDate
				? `From : ${formatShortDate(maternityDate)}`
				: 'Yes'
			: yesNoLabel(employment?.maternityLeave)
	// Cross-branch row (data-map §11; v1 renders it for ANY status when
	// present). The form collects it inside the not-employed accordion too
	// (EmploymentStep), so dropping it there would lose real answers.
	// Form hides the question when no child is listed (student housing has no
	// children stepper). Don't leave a "-" box on reports that never asked it.
	const showMaternity =
		!showStudentExtras &&
		hasAnyChildren(propertyStep as Record<string, unknown> | undefined)
	const maternityField = showMaternity ? (
		<FieldBox label="Maternity/Paternity Leave" value={maternityValue} />
	) : null
	const maternityRow =
		showMaternity && employment?.maternityLeave ? (
			<FieldBox label="Maternity/Paternity Leave" value={maternityValue} />
		) : null

	const studentReceivingStudentFinancialAssistance =
		employmentExtras.receivingStudentFinancialAssistance as string | undefined
	const studentReceivingParentalAssistance =
		employmentExtras.receivingParentalAssistance as string | undefined

	const studentGovernmentAssistanceValue = isEmployed
		? employment?.governmentAssistance
		: isNotEmployed
			? employment?.notEmployedGovernmentAssistance
			: isNoHistory
				? employment?.noHistoryGovernmentAssistance
				: undefined

	const studentFinancialRows =
		studentReceivingStudentFinancialAssistance === 'yes'
			? deriveStudentFinancialRows(
					employment as Parameters<typeof deriveStudentFinancialRows>[0],
				)
			: []

	// Work Authorization renders for EVERY kind now (QA 2026-09) — the
	// screening asks it on the Employment step; legacy rental-step answers
	// arrive through the resolved prop. Falls back to the employment step's
	// own keys when no resolved value was passed (bare/test renders).
	const resolvedWorkAuth: ResolvedWorkAuthorization = workAuth ?? {
		status: employment?.immigrationStatus,
		expiration: employment?.immigrationStatusExpiration,
	}
	const workAuthFields = (
		<>
			<FieldBox
				label="Work Authorization Status"
				value={optionLabel(resolvedWorkAuth.status)}
			/>
			{/* Expiration only applies to visa statuses; Permanent Resident /
			    Canadian Citizen / Refugee never carry one, so a stale value left
			    behind by a later status switch must not surface (TT2-509). */}
			{workAuthStatusHasExpiration(resolvedWorkAuth.status) ? (
				<FieldBox
					label="Expiration Date"
					value={(() => {
						const raw = resolvedWorkAuth.expiration
						if (!raw?.trim()) return undefined
						if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return formatShortDate(raw)
						return raw
					})()}
				/>
			) : null}
		</>
	)

	const current: EmploymentEntry = {
		companyName: employment?.companyName,
		occupation: employment?.occupation,
		annualIncome: employment?.annualIncome,
		companyPhone: employment?.companyPhone,
		workAddress: employment?.workAddress,
		yearsOfEmployment: employment?.yearsOfEmployment,
		employmentType: employment?.employmentType,
		sourceOfIncome: employment?.currentSourceOfIncome,
	}
	const previous: EmploymentEntry = {
		companyName: employment?.previousCompanyName,
		occupation: employment?.previousOccupation,
		annualIncome: employment?.previousAnnualIncome,
		companyPhone: employment?.previousCompanyPhone,
		workAddress: employment?.previousWorkAddress,
		yearsOfEmployment: employment?.previousYearsOfEmployment,
		employmentType: employment?.previousEmploymentType,
		sourceOfIncome: employment?.previousSourceOfIncome,
	}
	// v1's presence rules: previous renders only when ≥1 mapped field is
	// non-empty (v1:6446-6450); additional entries are filtered the same way
	// AFTER mapping, so stray passthrough keys can't resurrect empty entries.
	const hasPrevious = hasAnyNonEmptyValue(previous)
	const additional = (employment?.additionalEmployments ?? [])
		.map(
			(entry): EmploymentEntry => ({
				companyName: entry.companyName,
				occupation: entry.occupation,
				annualIncome: entry.annualIncome,
				companyPhone: entry.companyPhone,
				workAddress: entry.workAddress,
				yearsOfEmployment: entry.yearsOfEmployment,
				employmentType: entry.employmentType,
				sourceOfIncome: entry.sourceOfIncome,
			}),
		)
		.filter((entry) => hasAnyNonEmptyValue(entry))

	const currentCard = (withSourceOfIncome: boolean) => (
		<SectionCard
			tone="green"
			icon={<BriefcaseBusiness />}
			title="Current Employment"
			className="rounded-2xl"
		>
			<EmploymentFields
				entry={current}
				withSourceOfIncome={withSourceOfIncome}
				phoneFieldOn={employerPhoneOn}
				workAddressFieldOn={employerWorkAddressOn}
			>
				{workAuthFields}

				{maternityField}
			</EmploymentFields>
		</SectionCard>
	)
	// The dates EmploymentStep's "First & Last Day of Work" range picker
	// collected, which `previousYearsOfEmployment` is derived from (client
	// 2026-08-27). Rendered on the previous card itself rather than through
	// `extra`, so BOTH call sites get them — employed-with-history and
	// not-employed. Each drops out when unstored: older applications kept the
	// derived span alone.
	// The range picker's two ends read as one span (client 2026-08-27), so they
	// share a field. Either end can be missing on older rows — fall back to the
	// one that exists rather than dropping the field.
	const previousWorkSpan = (() => {
		const from = employment?.previousEmploymentStartDate
			? formatDate(employment.previousEmploymentStartDate)
			: null
		const to = employment?.previousEmploymentEndDate
			? formatDate(employment.previousEmploymentEndDate)
			: null
		if (from && to) return `${from} & ${to}`
		return from ?? to
	})()
	const previousWorkDates = previousWorkSpan ? (
		<FieldBox label="First & Last Day Of Work" value={previousWorkSpan} />
	) : null
	const previousCard = (extra?: ReactNode) => (
		<SectionCard
			// Brand glyph (client 2026-09-07) — Current keeps the lucide briefcase.
			iconSrc="/img/previous-employment.svg"
			title="Previous Employment"
			className="rounded-2xl"
		>
			<EmploymentFields
				entry={previous}
				companyLabel="Previous Company"
				phoneFieldOn={employerPhoneOn}
				workAddressFieldOn={employerWorkAddressOn}
			>
				{previousWorkDates}
				{extra}
			</EmploymentFields>
		</SectionCard>
	)

	let cards: ReactNode
	if (isEmployed) {
		cards = (
			<>
				{currentCard(true)}
				{hasPrevious ? previousCard() : null}
			</>
		)
	} else if (isNotEmployed) {
		cards = previousCard(
			<>
				{workAuthFields}
				{maternityRow}
			</>,
		)
	} else if (isNoHistory) {
		cards = (
			<SectionCard
				tone="green"
				icon={<BriefcaseBusiness />}
				title="No Employment History"
				className="rounded-2xl"
			>
				<FieldBox
					label="Source Of Income"
					value={optionLabel(employment?.sourceOfIncome)}
				/>
				<FieldBox
					label="Annual Income"
					value={formatMoneyValue(employment?.noHistoryAnnualIncome)}
				/>
				{workAuthFields}
				{maternityRow}
			</SectionCard>
		)
	} else {
		// other/missing status — legacy ungated two-card shape, unchanged.
		cards = (
			<>
				{currentCard(false)}
				{previousCard()}
			</>
		)
	}

	const hasCards = Boolean(isEmployed || isNotEmployed || isNoHistory)

	// Prebuilt student questions are template-gated (TT-65): a row renders when
	// the frozen config keeps it on OR an answer exists. Legacy student apps
	// resolve to the student default (all on) → unchanged.
	const showGovernmentAssistance =
		Boolean(showStudentExtras) &&
		(isPrebuiltQuestionOn(
			formConfig,
			'employment_questions',
			'government_assistance',
		) ||
			Boolean(studentGovernmentAssistanceValue))
	const showStudentFinancial =
		Boolean(showStudentExtras) &&
		(isPrebuiltQuestionOn(
			formConfig,
			'employment_questions',
			'student_financial_assistance',
		) ||
			Boolean(studentReceivingStudentFinancialAssistance))
	const showParentalAssistance =
		Boolean(showStudentExtras) &&
		(isPrebuiltQuestionOn(
			formConfig,
			'employment_questions',
			'parental_assistance',
		) ||
			Boolean(studentReceivingParentalAssistance))
	// Custom questions render for every kind that answered them — the standard
	// flow now collects them too (dynamic Application Form, TT-65).
	const customRows = deriveCustomQuestionRows(
		formConfig,
		'employment_questions',
		employment?.customQuestionAnswers,
	)
	const showAdditionalQuestions =
		showGovernmentAssistance ||
		showStudentFinancial ||
		showParentalAssistance ||
		customRows.length > 0

	return hasCards || showAdditionalQuestions ? (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7')}>
			<div className="flex flex-wrap items-center gap-3">
				<IconSquare tone="rose" className="bg-[#F6ABCB1A]">
					<BriefcaseBusiness />
				</IconSquare>
				<h2 className="text-[19px] font-semibold text-[#32302F]">Employment</h2>
				{/* {statusLabel ? (
					<PastelChip tone={pillTone} label={statusLabel} />
				) : null} */}
			</div>

			{hasCards ? (
				<div className="mt-5 grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
					{cards}
				</div>
			) : null}

			{isEmployed && additional.length > 0 ? (
				<div className="mt-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
					{additional.map((entry, index) => (
						<SectionCard
							key={`additional-${index}`}
							tone="green"
							icon={<BriefcaseBusiness />}
							title={`Additional Employment ${index + 1}`}
							className="rounded-2xl"
						>
							<EmploymentFields
								entry={entry}
								withSourceOfIncome
								phoneFieldOn={employerPhoneOn}
								workAddressFieldOn={employerWorkAddressOn}
							/>
						</SectionCard>
					))}
				</div>
			) : null}

			{showAdditionalQuestions ? (
				// Full-width card (Figma, QA 2026-09-08) — not a half column.
				<div className="mt-6">
					<SectionCard
						tone="green"
						icon={<BriefcaseBusiness />}
						title="Additional Questions"
						className="rounded-2xl"
						columns={1}
					>
						{showGovernmentAssistance ? (
							<FieldBox
								label={questionLabel('Are you on any government assistance?')}
								value={yesNoLabel(studentGovernmentAssistanceValue)}
							/>
						) : null}

						{showStudentFinancial ? (
							<FieldBox
								label={questionLabel(
									'Are you receiving student financial assistance (scholarships, grants, loans)?',
								)}
								value={yesNoLabel(studentReceivingStudentFinancialAssistance)}
							/>
						) : null}

						{showStudentFinancial &&
						studentReceivingStudentFinancialAssistance === 'yes' &&
						studentFinancialRows.length > 0 ? (
							<div className="rounded-[15px] border border-[#F0F0F0] p-4">
								<p className="mb-4 text-[14px] font-semibold text-[#32302F]">
									Financial Assistance
								</p>
								<div className="space-y-4">
									{studentFinancialRows.map((r) => (
										<div
											key={`${r.type}-${r.frequency}-${r.grossIncome}`}
											className="grid grid-cols-1 gap-4 sm:grid-cols-3"
										>
											<FieldBox label="Type" value={r.type} />
											<FieldBox
												label="Gross Income"
												value={formatMoneyValue(r.grossIncome)}
											/>
											<FieldBox label="Frequency" value={r.frequency} />
										</div>
									))}
								</div>
							</div>
						) : null}

						{showParentalAssistance ? (
							<FieldBox
								label={questionLabel(
									'Are you receiving parental assistance or any other type of income?',
								)}
								value={yesNoLabel(studentReceivingParentalAssistance)}
							/>
						) : null}

						{showParentalAssistance &&
						studentReceivingParentalAssistance === 'yes' ? (
							<div className="rounded-[15px] border border-[#F0F0F0] p-4">
								<p className="mb-4 text-[14px] font-semibold text-[#32302F]">
									Parental Assistance / Other Income
								</p>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
									<FieldBox
										label="Source"
										value={
											employmentExtras.parentalAssistanceSource as
												| string
												| undefined
										}
									/>
									<FieldBox
										label="Gross Amount"
										value={formatMoneyValue(
											employmentExtras.parentalAssistanceGrossAmount as
												| string
												| undefined,
										)}
									/>
									<FieldBox
										label="Frequency"
										value={
											employmentExtras.parentalAssistanceFrequency as
												| string
												| undefined
										}
									/>
								</div>
							</div>
						) : null}
						<CustomQuestionAnswerList rows={customRows} />
					</SectionCard>
				</div>
			) : null}
		</section>
	) : null
}
